import { useMemo, useRef, useState } from "react";
import { Dialog, FormControl, Text, Textarea } from "@primer/react";
import { GraphIcon, NumberIcon, UploadIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetIdentity, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ComplianceImpact } from "../components/ComplianceMetric/ComplianceMetric.jsx";
import { EditablePercentage, formatPercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { FormContainer } from "../components/FormContainer/FormContainer.jsx";
import { EmptyState } from "../components/EmptyState/EmptyState.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../components/PortfolioValue/PortfolioValue.jsx";
import { PortfolioValueDialog } from "../components/PortfolioValue/PortfolioValueDialog.jsx";
import { adjustAllocationsToTotal, parseAllocationImport, sortAllocationsDescending } from "../domain/allocationImport.js";
import { assetComplianceImpacts, evaluateCompliance } from "../domain/compliance.js";
import { sortAssetsByAllocation, updateAllocationSnapshot } from "../domain/document.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import "./AllocationPage.css";

export function AllocationPage({ repository }) {
  const application = useApplicationDocument(repository);

  if (application.error && !application.document) {
    return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  }
  if (!application.document) return <AllocationLoading />;

  return (
    <article className="allocation-page">
      {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
      {application.document.assets.length === 0 ? (
        <><PageHeading description="Record the current percentage held in each asset.">Allocation</PageHeading><EmptyState
            icon={GraphIcon}
            heading="No assets to allocate"
            description="Add assets before recording a portfolio allocation."
            action={<Button as="a" href="#/assets">Go to Assets</Button>}
          /></>
      ) : (
        <AllocationEditor application={application} key={application.profile?.id} />
      )}
    </article>
  );
}

function AllocationEditor({ application }) {
  const { document } = application;
  const values = allocationValues(document);
  const [orderValues, setOrderValues] = useState(values);
  const savingAllocation = useRef(false);
  const [importError, setImportError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [portfolioValueOpen, setPortfolioValueOpen] = useState(false);
  const portfolioValueButton = useRef(null);
  const portfolioValue = usePortfolioValue();
  const total = Object.values(values).reduce((sum, value) => sum + value, 0);
  const showPortfolioValue = portfolioValue.value !== null;
  const sortedAssets = useMemo(() => sortAssetsForAllocation(document.assets, orderValues), [document.assets, orderValues]);
  const impacts = useMemo(() => assetComplianceImpacts(document, evaluateCompliance(document)), [document]);

  async function saveAllocation(assetId, basisPoints) {
    if (savingAllocation.current || application.saving) return false;
    savingAllocation.current = true;
    try {
      const allocations = document.allocations.filter((allocation) => allocation.assetId !== assetId);
      return await application.save(updateAllocationSnapshot(document, [...allocations, { assetId, basisPoints }]));
    } finally {
      savingAllocation.current = false;
    }
  }

  async function replaceAllocation(importedAllocations) {
    setImportError(null);
    try {
      const nextDocument = sortAssetsByAllocation(updateAllocationSnapshot(document, importedAllocations));
      if (!await application.save(nextDocument)) return false;
      setOrderValues(allocationValues(nextDocument));
      setImportOpen(false);
      return true;
    } catch (error) {
      setImportError(error.message);
      return false;
    }
  }

  return (
    <>
      <PageHeading description="Record the current percentage held in each asset. Edits save automatically." actions={<><Button ref={portfolioValueButton} tone="secondary" leadingVisual={NumberIcon} onClick={() => setPortfolioValueOpen(true)}>Portfolio value</Button><Button tone="secondary" leadingVisual={UploadIcon} disabled={application.saving} onClick={() => setImportOpen(true)}>Import allocations</Button></>}>Allocation</PageHeading>
      {importError ? <Alert tone="danger" heading="Import was not saved">{importError}</Alert> : null}
      <AllocationTotal basisPoints={total} />
      <FormContainer className={`allocation-editor${showPortfolioValue ? " with-portfolio-value" : ""}`}>
            <div className="allocation-heading"><strong>Allocation</strong>{showPortfolioValue ? <strong className="portfolio-value-heading">Value</strong> : null}<strong>Asset</strong></div>
            <div className="allocation-list">
              {sortedAssets.map((asset) => <div className="allocation-row" key={asset.id}>
                <AssetIdentity asset={asset} href={assetHref(asset.id)} trailing={<ComplianceImpact impact={impacts.get(asset.id)} />} variant="responsive" />
                <div className="allocation-figures">
                  <div className="allocation-value"><EditablePercentage allowEmpty disabled={application.saving} label={`${asset.id} allocation`} value={values[asset.id]} onSave={(basisPoints) => saveAllocation(asset.id, basisPoints)} /></div>
                  {showPortfolioValue ? <Text className="portfolio-amount">{formatPortfolioAmount(amountForBasisPoints(portfolioValue.value, values[asset.id]))}</Text> : null}
                </div>
              </div>)}
            </div>
      </FormContainer>
      {portfolioValueOpen ? <PortfolioValueDialog onClose={() => setPortfolioValueOpen(false)} returnFocusRef={portfolioValueButton} /> : null}
      {importOpen ? <AllocationImportDialog assets={document.assets} saving={application.saving} onClose={() => setImportOpen(false)} onReplace={replaceAllocation} /> : null}
    </>
  );
}

function sortAssetsForAllocation(assets, values) {
  return [...assets].sort((left, right) => (
    (values[right.id] ?? 0) - (values[left.id] ?? 0)
    || (left.name || left.id).localeCompare(right.name || right.id, undefined, { numeric: true, sensitivity: "base" })
    || left.id.localeCompare(right.id, undefined, { numeric: true, sensitivity: "base" })
  ));
}

function AllocationImportDialog({ assets, onClose, onReplace, saving }) {
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);

  function reviewImport() {
    try {
      const result = parseAllocationImport(content, assets);
      setPreview({ ...result, allocations: sortAllocationsDescending(result.allocations) });
      setError(null);
    } catch (parseError) {
      setPreview(null);
      setError(parseError);
    }
  }

  async function replaceImport() {
    if (!await onReplace(preview.allocations)) {
      setError(new Error("The allocation could not be replaced."));
    }
  }

  function adjustImport() {
    const allocations = sortAllocationsDescending(adjustAllocationsToTotal(preview.allocations));
    setPreview({ ...preview, allocations, totalBasisPoints: 10000 });
    setError(null);
  }

  async function readFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setContent(await file.text());
      setFileName(file.name);
      setPreview(null);
      setError(null);
    } catch (readError) {
      setError(new Error(`Could not read “${file.name}”: ${readError.message}`));
    }
  }

  const footerButtons = preview ? [
    { content: "Back", onClick: () => { setPreview(null); setError(null); } },
    { content: saving ? "Replacing…" : "Replace allocations", buttonType: "primary", onClick: replaceImport, disabled: saving }
  ] : [
    { content: "Cancel", onClick: onClose },
    { content: "Review import", buttonType: "primary", onClick: reviewImport, disabled: content.trim().length === 0 }
  ];

  return <Dialog className="allocation-import-dialog" title="Import allocations" subtitle={preview ? "Review the extracted allocation before replacing the current snapshot." : "Paste allocation data or choose a text-based file."} onClose={onClose} footerButtons={footerButtons}>
    {preview ? <AllocationImportPreview assets={assets} error={error} onAdjust={adjustImport} preview={preview} /> : <div className="allocation-import-input">
      {error ? <ImportError error={error} /> : null}
      <FormControl>
        <FormControl.Label>Allocation data</FormControl.Label>
        <FormControl.Caption>Include known asset IDs and percentages in matching appearance order.</FormControl.Caption>
        <Textarea value={content} onChange={(event) => { setContent(event.target.value); setFileName(null); setError(null); }} rows={10} resize="vertical" block autoFocus placeholder={"GLD 25%\nCASH 75%"} />
      </FormControl>
      <input ref={fileInput} className="allocation-import-file" type="file" accept=".csv,.txt,.xml,text/csv,text/plain,application/xml,text/xml" onChange={readFile} />
      <div className="allocation-import-file-action"><Button tone="secondary" onClick={() => fileInput.current?.click()}>Choose file</Button>{fileName ? <Text>{fileName}</Text> : null}</div>
    </div>}
  </Dialog>;
}

function ImportError({ error }) {
  const stats = error.stats;
  return <Alert tone="danger" heading="Unable to recognize this allocation">
    {error.message}{stats ? ` Found ${stats.assetCount} ${stats.assetCount === 1 ? "asset ID" : "asset IDs"} and ${stats.percentageCount} ${stats.percentageCount === 1 ? "percentage" : "percentages"}.` : ""}
  </Alert>;
}

function AllocationImportPreview({ assets, error, onAdjust, preview }) {
  const assetsById = new Map(assets.map((asset) => [asset.id, asset]));
  const canAdjust = preview.totalBasisPoints !== 10000 && preview.totalBasisPoints >= 9900 && preview.totalBasisPoints <= 10100;
  return <div className="allocation-import-preview">
    {error ? <ImportError error={error} /> : null}
    <AllocationTotal basisPoints={preview.totalBasisPoints} />
    {canAdjust ? <div className="allocation-import-adjust"><Text>Close enough to 100% to correct rounding differences.</Text><Button tone="secondary" onClick={onAdjust}>Adjust to 100%</Button></div> : null}
    <div className="allocation-import-table">
      <div className="allocation-import-heading"><strong>Allocation</strong><strong>Asset</strong></div>
      {preview.allocations.map((allocation) => <div className="allocation-import-row" key={allocation.assetId}>
        <strong>{formatPercentage(allocation.basisPoints)}%</strong>
        <AssetIdentity asset={assetsById.get(allocation.assetId)} href={assetHref(allocation.assetId)} />
      </div>)}
    </div>
    <Alert tone="warning" heading="Replace the current allocation?">Assets not shown in this preview will be set to 0%.</Alert>
  </div>;
}

function allocationValues(document) {
  const allocations = document.allocations ?? [];
  return Object.fromEntries(document.assets.map((asset) => {
    const basisPoints = allocations.find((allocation) => allocation.assetId === asset.id)?.basisPoints ?? 0;
    return [asset.id, basisPoints];
  }));
}

function AllocationTotal({ basisPoints }) {
  const difference = basisPoints - 10000;
  const state = difference === 0 ? "complete" : difference < 0 ? "under" : "over";
  return (
    <section className={`allocation-total ${state}`} aria-live="polite">
      <div><span><Text>Portfolio total</Text><strong>{formatPercentage(basisPoints)}%</strong></span><Text>{state === "complete" ? "Fully allocated" : `${formatPercentage(Math.abs(difference))}% ${state === "under" ? "unallocated" : "overallocated"}`}</Text></div>
      <progress aria-label="Portfolio allocation total" max="10000" value={Math.min(basisPoints, 10000)} />
    </section>
  );
}

function AllocationLoading() {
  return <article className="allocation-page"><PageHeading description="Loading allocation…">Allocation</PageHeading></article>;
}
